import type { ActivityEvent } from "@/hooks/use-activity";
import { localDayKey } from "./upcoming-posts";

/** How an event turned out, which drives its color and icon. */
export type ActivityTone = "success" | "failed" | "scheduled" | "cancelled" | "pending" | "info";

/**
 * One row in the feed. Post rows that went out together (a post plus its
 * Story/Short companions) with the same outcome collapse into one item.
 */
export interface ActivityItem {
  key: string;
  at: string;
  tone: ActivityTone;
  /** The newest event, which represents the item. */
  lead: ActivityEvent;
  events: ActivityEvent[];
}

export interface ActivityDay {
  key: string;
  date: Date;
  items: ActivityItem[];
}

export function toneOf(event: ActivityEvent): ActivityTone {
  if (event.post) {
    return (
      {
        PUBLISHED: "success",
        FAILED: "failed",
        SCHEDULED: "scheduled",
        CANCELLED: "cancelled",
      } as const
    )[event.post.status];
  }
  // Other sources only carry a server-built English title; its status words are fixed.
  const title = event.title.toLowerCase();
  if (/\b(failed|rejected)\b/.test(title)) return "failed";
  if (/\b(waiting|pending|running)\b/.test(title)) return "pending";
  if (/\b(skipped|cancelled|paused)\b/.test(title)) return "cancelled";
  if (event.type === "boost" && /\bsuggested\b/.test(title)) return "info";
  return "success";
}

/** Newest-first events → day sections of items, keeping the feed's order. */
export function groupActivity(events: readonly ActivityEvent[]): ActivityDay[] {
  const days: ActivityDay[] = [];
  const byKey = new Map<string, ActivityItem>();

  for (const event of events) {
    const date = new Date(event.at);
    const dayKey = localDayKey(date);
    let day = days[days.length - 1];
    if (!day || day.key !== dayKey) {
      day = { key: dayKey, date, items: [] };
      days.push(day);
    }

    const tone = toneOf(event);
    const groupKey = event.post?.groupId ? `${dayKey}|${event.post.groupId}|${event.post.status}` : null;
    const existing = groupKey ? byKey.get(groupKey) : undefined;
    if (existing) {
      existing.events.push(event);
      continue;
    }
    const item: ActivityItem = { key: `${event.type}-${event.id}`, at: event.at, tone, lead: event, events: [event] };
    if (groupKey) byKey.set(groupKey, item);
    day.items.push(item);
  }
  return days;
}
